/*
Global Search — the canonical contract for POST /api/v2/search
(restServer `apiv2` app), consumed by web's GlobalSearch modal. Shared between
the two, NOT published: the route is excluded from /api/v2/schema/, which is what
lets these shapes change without breaking an external promise. ONE endpoint,
always multipart/form-data: the text query is a form field and an optional
`image` part turns the same request into a reverse-image search. There is no
JSON request body and no separate image endpoint — query modality (text vs
image) is an input detail of one search resource, not a different resource, and
both modalities return the identical SearchResponse. The Python serializer in
restServer mirrors these shapes and must be kept in lockstep.

Contract invariants:
- The server's internal identity is the `id` (source_key). The web app navigates
	by `nativeIds` through one routing helper (never by parsing URLs); a
	destination's `url` serves copy-link / new-tab / external.
- Counts are displayed counts, never corpus totals (`hasMore` signals more).
- No pagination: each category returns its top results in one response, and the
	client reveals the rest of a category ("View all") from what it already has.
	`hasMore` means more than the per-category cap matched — the user refines to
	narrow, there is no "next page" to fetch.
- A request carries exactly one of a text `query` or an `image` part. An image
	search is relevance-only, finds images and videos only, and never persists
	query bytes/hashes/vectors.
*/

export type SearchContentType =
	'image' |
	'video' |
	'tool' |
	'board' |
	'template' |
	'article'
	;

export type SearchSource =
	'library' |
	'tools' |
	'boards' |
	'templates' |
	'articles'
	;

// Query Image intake limits, mirroring the server's (apiv2/views/search.py),
// which checks them again. The browser sends the file as picked; the server
// normalizes it before embedding.
export const SEARCH_IMAGE_MAX_BYTES: number = 10 * 1024 * 1024;
export const SEARCH_IMAGE_MAX_MEGAPIXELS: number = 40;
export const SEARCH_IMAGE_ALLOWED_MIME_TYPES: string[] = ['image/png', 'image/jpeg', 'image/webp'];

// External article destinations must live on an owned host. The website
// (www.rundiffusion.com) is canonical for BOTH article sources — it serves
// git-backed articles and still-published Ghost posts at root slugs, and
// learn.rundiffusion.com paths 301 there — but learn stays allowlisted for
// any legacy URL.
export const SEARCH_ARTICLE_URL_ALLOWED_HOSTS: string[] = [
	'www.rundiffusion.com',
	'rundiffusion.com',
	'learn.rundiffusion.com',
];

/*
Destinations. `kind` + `nativeIds` drive in-app navigation (the single
searchRouting helper maps them to vue-router locations); `url` is the canonical
permalink string. Internal kinds carry their ids; external articles are
url-only.
*/
export interface SearchDestinationLibraryResult {
	kind: 'LIBRARY_RESULT'
	label: string
	url: string // /runnit/library?nodeRunId=...&resultUuid=...
	nativeIds: { nodeRunId: string, resultUuid: string }
}
export interface SearchDestinationBoard {
	kind: 'BOARD'
	label: string
	url: string // /runnit/:runnitId
	nativeIds: { runnitId: string }
}
export interface SearchDestinationTemplate {
	kind: 'TEMPLATE'
	label: string
	url: string // /runnit/:runnitId (board route renders the template view)
	nativeIds: { templateId: string }
}
export interface SearchDestinationTool {
	kind: 'TOOL'
	label: string
	url: string // /runnit/reroute?toolId=:nodeDefId
	nativeIds: { nodeDefId: string }
}
export interface SearchDestinationExternalArticle {
	kind: 'EXTERNAL_ARTICLE'
	label: string
	url: string // https://www.rundiffusion.com/... (allowlisted hosts only)
}
export type SearchDestination =
	SearchDestinationLibraryResult |
	SearchDestinationBoard |
	SearchDestinationTemplate |
	SearchDestinationTool |
	SearchDestinationExternalArticle
	;

export interface SearchItemThumbnail {
	storagePath?: string // GCS storage path (e.g. the _512x512.webp thumb) — the modal resolves it to a download URL (getDownloadURL) and keeps it while open
	url?: string // absolute https URL (tool icons, article images)
	isNsfw?: boolean // client renders the NSFW placeholder tile
}

export interface SearchItemMetadata {
	toolTitle?: string // results: generating tool's title (subtitle composition)
	width?: number
	height?: number
	durationSec?: number // video results
	accessLevel?: 'PRIVATE' | 'SHARED' | 'RESTRICTED' // boards
	itemCount?: number // boards
	author?: string // templates
	articleExcerpt?: string // articles
}

export interface SearchItem {
	id: string // the source_key — the server's stable identity for this entity
	type: SearchContentType
	title: string
	subtitle?: string
	thumbnail?: SearchItemThumbnail
	metadata?: SearchItemMetadata
	destinations: SearchDestination[] // [0] = primary; the rest are secondary actions (e.g. result → "Open board")
	occurredAtIso?: string // native content date; Results rows show it after the subtitle ("Flux · Oct 2")
}

export interface SearchGroup {
	source: SearchSource
	hasMore: boolean // more than the per-category cap matched — client shows a "refine" hint
	items: SearchItem[]
}

/*
The request is ALWAYS sent as multipart/form-data (never JSON). The scalar
fields below map to same-named form parts; `types` is sent as repeated `types`
parts; the `image` File (⊆ the SEARCH_IMAGE_* limits) is appended as a binary
part and turns the request into a reverse-image search. A request carries
exactly one of `query` or `image`. There is no pagination: the server returns
each category's top results in one shot.
*/
export interface SearchRequest {
	query?: string // 2..500 chars (server-enforced); exactly one of `query` / `image`
	types?: SearchContentType[] // omitted = All ('image' / 'video' only when `image` is sent)
	image?: Blob // raw reverse-image-search file, unmodified; sent as the multipart `image` part
}

export interface SearchResponse {
	groups: SearchGroup[]
}
